import { z } from 'zod';

import { isMoney, type Money } from '@nucleus-commerce/core';

import { MAX_DURATION_DAYS, MAX_INCREMENT_TIERS, REASON_MAX } from './auction-rules';

const LOCAL_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
/** "15", "12.5", "7.25": a percentage with at most two decimals, read without a float. */
const PERCENT = /^(\d{1,2})(?:\.(\d{1,2}))?$/;
const WHOLE = /^\d{1,2}$/;
const MINUTE = 60_000;

export function isLocalDateTime(value: string): boolean {
  return LOCAL_INPUT.test(value);
}

/** "12.5" → 1250 basis points; null for anything that is not a plain percentage. */
export function percentToBps(value: string): number | null {
  const match = PERCENT.exec(value.trim());
  if (!match) return null;
  const whole = Number(match[1]);
  const fraction = Number((match[2] ?? '').padEnd(2, '0'));
  return whole * 100 + fraction;
}

/** 1250 → "12.5"; the inverse of `percentToBps` for the edit form. */
export function bpsToPercent(bps: number | null): string {
  if (bps === null) return '';
  const whole = Math.floor(bps / 100);
  const fraction = String(bps % 100)
    .padStart(2, '0')
    .replace(/0+$/, '');
  return fraction ? `${whole}.${fraction}` : String(whole);
}

const tier = z.object({
  from: z.custom<Money>(isMoney).nullable(),
  step: z.custom<Money>(isMoney).nullable(),
});

/**
 * The auction form. Times are `datetime-local` values on the store's clock; `startsAtMs` and
 * `endsAtMs` are those instants, resolved by the caller with the store time zone so this schema
 * never guesses one.
 */
export function auctionFormSchema(toInstant: (value: string) => number | null) {
  return z
    .object({
      storeProductId: z.string(),
      variantId: z.string(),
      startsAt: z.string(),
      endsAt: z.string(),
      startingPrice: z.custom<Money>(isMoney).nullable(),
      reservePrice: z.custom<Money>(isMoney).nullable(),
      buyNowPrice: z.custom<Money>(isMoney).nullable(),
      incrementTable: z.array(tier),
      antiSnipeMinutes: z.string().trim(),
      buyerPremiumPercent: z.string().trim(),
    })
    .superRefine((v, ctx) => {
      const issue = (path: Array<string | number>, message: string) =>
        ctx.addIssue({ code: 'custom', path, message });
      if (!v.storeProductId) issue(['storeProductId'], 'auctions:validation.lotRequired');
      else if (!v.variantId) issue(['variantId'], 'auctions:validation.variantRequired');

      const starts = isLocalDateTime(v.startsAt) ? toInstant(v.startsAt) : null;
      const ends = isLocalDateTime(v.endsAt) ? toInstant(v.endsAt) : null;
      if (starts === null) issue(['startsAt'], 'auctions:validation.dateTime');
      if (ends === null) issue(['endsAt'], 'auctions:validation.dateTime');
      if (starts !== null && ends !== null) {
        if (ends - starts < MINUTE) issue(['endsAt'], 'auctions:validation.endAfterStart');
        else if (ends - starts > MAX_DURATION_DAYS * 24 * 60 * MINUTE) {
          issue(['endsAt'], 'auctions:validation.maxDuration');
        }
      }

      if (v.startingPrice === null) issue(['startingPrice'], 'auctions:validation.priceRequired');
      else if (v.startingPrice.amount <= 0) {
        issue(['startingPrice'], 'auctions:validation.pricePositive');
      }
      if (v.reservePrice && v.startingPrice && v.reservePrice.amount < v.startingPrice.amount) {
        issue(['reservePrice'], 'auctions:validation.reserveBelowStart');
      }
      if (v.buyNowPrice && v.startingPrice && v.buyNowPrice.amount <= v.startingPrice.amount) {
        issue(['buyNowPrice'], 'auctions:validation.buyNowAboveStart');
      } else if (v.buyNowPrice && v.reservePrice && v.buyNowPrice.amount < v.reservePrice.amount) {
        issue(['buyNowPrice'], 'auctions:validation.buyNowBelowReserve');
      }

      if (v.incrementTable.length === 0) {
        issue(['incrementTable'], 'auctions:validation.tiersRequired');
      } else if (v.incrementTable.length > MAX_INCREMENT_TIERS) {
        issue(['incrementTable'], 'auctions:validation.tiersMax');
      }
      v.incrementTable.forEach((row, index) => {
        if (row.from === null)
          issue(['incrementTable', index, 'from'], 'auctions:validation.tierFrom');
        else if (index === 0 && row.from.amount !== 0) {
          issue(['incrementTable', index, 'from'], 'auctions:validation.firstTierZero');
        } else if (index > 0) {
          const previous = v.incrementTable[index - 1]?.from;
          if (previous && row.from.amount <= previous.amount) {
            issue(['incrementTable', index, 'from'], 'auctions:validation.tiersAscending');
          }
        }
        if (row.step === null || row.step.amount <= 0) {
          issue(['incrementTable', index, 'step'], 'auctions:validation.tierStep');
        }
      });

      if (!WHOLE.test(v.antiSnipeMinutes) || Number(v.antiSnipeMinutes) > 60) {
        issue(['antiSnipeMinutes'], 'auctions:validation.antiSnipe');
      }
      if (v.buyerPremiumPercent !== '') {
        const bps = percentToBps(v.buyerPremiumPercent);
        if (bps === null || bps < 1 || bps > 5_000) {
          issue(['buyerPremiumPercent'], 'auctions:validation.premium');
        }
      }
    });
}

export type AuctionFormInput = z.input<ReturnType<typeof auctionFormSchema>>;
export type AuctionFormOutput = z.output<ReturnType<typeof auctionFormSchema>>;

export const reasonSchema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, { error: 'auctions:validation.reasonRequired' })
    .max(REASON_MAX, { error: 'auctions:validation.reasonLength' }),
});

export type ReasonFormInput = z.input<typeof reasonSchema>;
export type ReasonFormOutput = z.output<typeof reasonSchema>;
